import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { NotificationManagerService } from '@iupics-manager/managers/notification-manager/notification-manager.service';
import { TranslateModule } from '@ngx-translate/core';
import { AuditFormat } from './audit-format';
import { formatDuration, isStale, prettyJson } from './audit-query';
import { AuditExecution, AuditExecutionDetail } from './audit.models';

/** Detail panel of a process execution: summary, parameters, persisted logs, produced files, links */
@Component({
  selector: 'iu-audit-execution-detail',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './audit-execution-detail.component.html',
  styleUrl: './audit-detail.scss',
})
export class AuditExecutionDetailComponent {
  readonly detail = input<AuditExecutionDetail | null>(null);
  /** row the panel was opened from: shown while the detail loads */
  readonly row = input<AuditExecution | null>(null);
  readonly loading = input(false);
  readonly error = input<string | null>(null);
  readonly fmt = input.required<AuditFormat>();
  readonly now = input.required<Date>();

  readonly closed = output<void>();
  readonly retry = output<void>();
  readonly showChanges = output<AuditExecution>();
  /** open another execution (sub-run, or the root run of a sub-run) */
  readonly openRun = output<number>();
  readonly filterProcess = output<AuditExecution>();
  readonly filterUser = output<AuditExecution>();
  /** stop request for a running execution (done server side) */
  readonly stop = output<AuditExecution>();
  /** role allowed to stop processes (administrator, as in "Process in progress") */
  readonly canStop = input(false);
  /** stop sent, waiting for the execution to end */
  readonly stopping = input(false);
  /** why the last stop did not end the run: message key or server message */
  readonly stopNote = input<string | null>(null);

  readonly #notifications = inject(NotificationManagerService);
  readonly logQuery = signal('');
  readonly copied = signal(false);
  /** second click confirms the stop */
  readonly confirmStop = signal(false);

  readonly execution = computed(() => this.detail()?.execution ?? this.row());
  readonly logs = computed(() => {
    const q = this.logQuery().trim().toLowerCase();
    const logs = this.detail()?.logs ?? [];
    return q ? logs.filter((l) => (l.message ?? '').toLowerCase().includes(q)) : logs;
  });
  readonly slower = computed(() => {
    const d = this.detail();
    const ms = d?.execution.durationMs;
    return d?.averageMs && ms && ms > d.averageMs * 1.5 && ms - d.averageMs > 5000 ? d.averageMs : null;
  });

  duration = formatDuration;
  pretty = prettyJson;

  stale(e: AuditExecution): boolean {
    return isStale(e.status, e.created, this.now());
  }

  statusKey(status: string): string {
    return `audit.status.${status}`;
  }

  askStop(e: AuditExecution): void {
    if (!this.confirmStop()) {
      this.confirmStop.set(true);
      return;
    }
    this.confirmStop.set(false);
    this.stop.emit(e);
  }

  /** same authenticated download as the notification of the execution */
  download(path: string): void {
    this.#notifications.downloadReport(path);
  }

  copyId(e: AuditExecution): void {
    navigator.clipboard?.writeText(String(e.id)).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    });
  }
}
